import { useEffect, useRef, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useVeil } from '../lib/useVeil'
import { maskText } from '../lib/security'

/**
 * ⌘K — the wayfinder. Search every soul, tree, and gathering you can reach,
 * plus quick actions. Arrow keys + Enter, Esc to close.
 */

type Item =
  | { kind: 'person'; id: string; label: string; sub: string; treeId: string }
  | { kind: 'tree'; id: string; label: string; sub: string }
  | { kind: 'event'; id: string; label: string; sub: string }
  | { kind: 'action'; id: string; label: string; sub: string; to: string }

const ACTIONS: Item[] = [
  { kind: 'action', id: 'a1', label: 'Plant a new tree', sub: 'action', to: '/trees' },
  { kind: 'action', id: 'a2', label: 'Plan a meet-up', sub: 'action', to: '/calendar' },
  { kind: 'action', id: 'a3', label: 'Open dashboard', sub: 'action', to: '/dashboard' },
  { kind: 'action', id: 'a4', label: 'Profile & plan', sub: 'action', to: '/profile' },
]

const ICON: Record<Item['kind'], string> = { person: '✨', tree: '🌳', event: '📅', action: '⚡' }

// recently-visited items (localStorage, newest first, capped)
const RECENTS_KEY = 'fa_palette_recents'
function loadRecents(): Item[] {
  try { return JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]') } catch { return [] }
}
function pushRecent(item: Item) {
  if (item.kind === 'action') return
  const next = [item, ...loadRecents().filter(r => !(r.kind === item.kind && r.id === item.id))].slice(0, 6)
  try { localStorage.setItem(RECENTS_KEY, JSON.stringify(next)) } catch { /* storage full */ }
}

export default function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [q, setQ] = useState('')
  const [items, setItems] = useState<Item[]>(ACTIONS)
  const [active, setActive] = useState(0)
  const [busy, setBusy] = useState(false)
  const veil = useVeil()
  const inputRef = useRef<HTMLInputElement>(null)
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null)
  const navigate = useNavigate()

  useEffect(() => {
    if (open) {
      const recents = loadRecents().map(r => ({ ...r, sub: `recent · ${r.sub.replace(/^recent · /, '')}` }))
      setQ(''); setItems([...recents, ...ACTIONS]); setActive(0)
      setTimeout(() => inputRef.current?.focus(), 30)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    if (debounce.current) clearTimeout(debounce.current)
    if (q.trim().length < 2) {
      const recents = q.trim() ? [] : loadRecents().map(r => ({ ...r, sub: `recent · ${r.sub.replace(/^recent · /, '')}` }))
      setItems([...recents, ...ACTIONS.filter(a => a.label.toLowerCase().includes(q.toLowerCase()))])
      setActive(0)
      return
    }
    debounce.current = setTimeout(async () => {
      setBusy(true)
      try {
        const res = await api.search(q.trim())
        const out: Item[] = [
          ...res.persons.map(p => ({
            kind: 'person' as const, id: p.id, treeId: p.tree_id,
            label: `${p.first_name} ${p.last_name}`.trim(),
            sub: `${p.tree_name}${p.birth_date ? ` · b.${p.birth_date.slice(0, 4)}` : ''}${p.is_living ? '' : ' ✝'}`,
          })),
          ...res.trees.map(t => ({ kind: 'tree' as const, id: t.id, label: t.name, sub: `${t.person_count} souls` })),
          ...res.events.map(e => ({ kind: 'event' as const, id: e.id, label: e.title, sub: `${e.start_at.slice(0, 10)} · ${e.tree_name}` })),
          ...ACTIONS.filter(a => a.label.toLowerCase().includes(q.toLowerCase())),
        ]
        setItems(out)
        setActive(0)
      } catch { /* keep old */ } finally { setBusy(false) }
    }, 220)
  }, [q, open])

  const go = useCallback((item: Item) => {
    pushRecent(item)
    onClose()
    if (item.kind === 'person') navigate(`/trees/${item.treeId}?person=${item.id}`)
    else if (item.kind === 'tree') navigate(`/trees/${item.id}`)
    else if (item.kind === 'event') navigate('/calendar')
    else navigate(item.to)
  }, [navigate, onClose])

  const onKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, items.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)) }
    else if (e.key === 'Enter' && items[active]) { e.preventDefault(); go(items[active]) }
    else if (e.key === 'Escape') onClose()
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-[60] bg-void-950/70 backdrop-blur-sm flex items-start justify-center pt-[12vh] px-4" onClick={onClose}>
      <div className="card-glass !bg-void-900/95 w-full max-w-lg shadow-2xl agent-enter overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-white/[0.08]">
          <span className="text-slate-500">🔍</span>
          <input ref={inputRef} value={q} onChange={e => setQ(e.target.value)} onKeyDown={onKey}
            placeholder="Search souls, trees, gatherings…"
            className="flex-1 bg-transparent outline-none text-sm text-slate-100 placeholder-slate-500" />
          {busy && <span className="w-3.5 h-3.5 rounded-full border-2 border-gold-400 border-t-transparent animate-spin" />}
          <kbd className="text-[10px] text-slate-600 border border-white/10 rounded px-1.5 py-0.5">esc</kbd>
        </div>
        <div className="max-h-[46vh] overflow-y-auto py-1.5">
          {items.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-8">Nothing in the sky by that name.</p>
          )}
          {items.map((item, i) => (
            <button key={`${item.kind}-${item.id}`} onClick={() => go(item)} onMouseEnter={() => setActive(i)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-left ${i === active ? 'bg-gold-500/10' : ''}`}>
              <span className="text-base">{ICON[item.kind]}</span>
              <span className="flex-1 min-w-0">
                <span className={`block text-sm truncate ${i === active ? 'text-gold-200' : 'text-slate-200'}`}>{item.kind === 'person' ? maskText(item.label, veil) : item.label}</span>
                <span className="block text-[11px] text-slate-500 truncate">{item.sub}</span>
              </span>
              {i === active && <kbd className="text-[10px] text-slate-600 border border-white/10 rounded px-1.5 py-0.5">↵</kbd>}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
